// Die Wirbelsäule der Welt (Konzept §12.2): ein handgeautortes, niedrig
// aufgelöstes Skelett. Streckenverlauf, Höhenprofil, Lage der Höfe, Gründe für
// jeden Bogen. Das Detail wird später deterministisch dazwischengefüllt.
//
// Koordinatensystem: X/Z ist die Ebene, Y ist oben (three.js-Konvention).
// "s" ist die Bogenlänge entlang *einer* Strecke in Metern.
//
// Streckennetz: Hauptstrecke (Süd↔Nord) plus eine abzweigende Nebenstrecke in
// eine Sackgasse (Konzept §2.1: Isolation statt „falscher Richtung"). Jede
// Strecke hat eigene Bogenlänge, eigenes Höhenprofil und eigene Features.
//
// API ist abwärtskompatibel: ohne Branch-Argument gilt immer die Hauptstrecke
// (`BRANCH_MAIN`). Client-Code, der `trackPoint(s)` ruft, bleibt gültig.
//
// Diese Datei ist reines JS ohne Abhängigkeiten: Server und Client bauen daraus
// exakt dieselbe Geometrie.

import { RAIL_GAUGE } from './constants.js';

// --- Branch-IDs -------------------------------------------------------------

export const BRANCH_MAIN = 'main';
export const BRANCH_MINE = 'mine';

// --- Handgeautortes Skelett: Hauptstrecke -----------------------------------
// Jeder Punkt hat einen Grund. Genau das fehlt prozeduraler Landschaft
// (Konzept §12.2), deshalb steht er hier als Kommentar mit im Code.
// Die Höhen sind so gewählt, dass keine Neigung 1,5 % übersteigt — mehr
// verträgt ein schwerer Güterzug ohne Schiebelok nicht, und die Strecke soll
// gebaut aussehen, nicht gezeichnet.
const MAIN_CONTROL_POINTS = [
  // x,      z,      y,     reason
  [   0,      0,    412, 'Südende: Wüstenebene, Streckenanfang'],
  [  40,   -300,    411, 'Ausfahrt Cinder Flats, schnurgerade über den Playa'],
  [  60,   -900,    409, 'gerade'],
  [  30,  -1500,    406, 'leichter Bogen, weil der alte Damm dem Wash folgt'],
  [ -80,  -2050,    402, 'Wash-Querung, tiefster Punkt der Südhälfte'],
  [-260,  -2520,    408, 'Beginn der Steigung, Bogen um den Basalthügel'],
  [-470,  -2900,    414, 'Einschnitt durch den Hügelrücken'],
  [-620,  -3300,    420, 'Damm über die Senke dahinter'],
  [-640,  -3760,  426.5, 'Bogen nach Nord, Steigung hält an'],
  [-540,  -4200,    433, 'gerade Rampe, Kiefern beginnen'],
  [-390,  -4620,    439, 'Kehre am Hangfuß'],
  [-330,  -5080,    444, 'Scheitelpunkt, Wasserturm links'],
  [-420,  -5520,    441, 'Gefälle nach Nord, Brücke über die Schlucht'],
  [-560,  -5960,    436, 'Waldstück, Sicht kurz'],
  [-600,  -6420,    431, 'Bogen in die Ebene von Mott'],
  [-520,  -6860,    428, 'Einfahrt Mott Junction'],
  [-460,  -7250,  427, 'Nordende: Ausziehgleis, dahinter unbefahrbar'],
];

// Weiche auf der Hauptstrecke: kurz vor der Wash-Querung, wo der alte
// Werksweg westlich abzweigte. s wird nach dem Sampling der Hauptstrecke
// auf den nächsten Stützpunkt gerundet und als JUNCTION_MAIN_S exportiert.
const JUNCTION_TARGET_S = 1840;

// --- Nebenstrecke: tote Kupfermine (Sackgasse) ------------------------------
// Beginnt an der Weiche, endet am Prellbock der aufgegebenen Verladung.
// Kein Durchfahrt, kein zweiter Ausgang — wer hierhin fährt, fährt nur zurück
// (Konzept §2.1: Isolation).
//
// Die Kontrollpunkte werden *nach* dem Bauen der Hauptstrecke gesetzt, damit
// der erste Punkt exakt auf der Weiche liegt (geometrische Stetigkeit).
// Vorläufige Zielpunkte in Weltkoordinaten; P0 wird überschrieben.
//
// Weichengeometrie (Konzept §12.2 — jeder Bogen hat einen Grund):
// Früher knickte P1 sofort ~29° ab → Straßengabelung statt Weiche.
// Echte Güterzug-Weiche (EW 190–300): Abzweigwinkel 6–8°, 9 m Gleisabstand
// erst nach 50–80 m, Radius ≥ 190 m. P1 liegt deshalb fast kollinear zur
// Hauptstrecke (Tangente bei s=0 muss passen, sonst springt der Zug-Heading
// am Branch-Wechsel), der Bogen öffnet sich erst danach.
const MINE_CONTROL_TARGETS = [
  // x,       z,        y,     reason — P0 = Weiche, exakt aus Hauptstrecke
  // Koordinaten aus Haupt-Frame (along, lat): (0,0)→(42,0.18)→(72,3.4)→(100,8.0)→(140,17)…
  // Ziel: EW-190/300-ähnlich (Abzweig 6–8°, 9 m Versatz erst nach ~70–100 m, R>180).
  [  -25.56, -1830.91,  402.99, 'Weiche: exakt Hauptstrecke, Tangente identisch (kein Knick beim Branch-Wechsel)'],
  [  -34.60, -1871.93,  402.96, 'Tangentialer Anlauf: ~42 m fast kollinear mit Haupt, winziger Versatz startet den EW-Bogen'],
  [  -44.08, -1900.57,  402.78, 'Weichenbogen R≈220: Zungen öffnen sich auf ~7°, Gleise noch eng beieinander'],
  [  -54.48, -1926.97,  402.58, 'Herzstück-Bereich: Abzweigwinkel ~7–8°, 9 m Gleisabstand zeichnet sich ab'],
  [  -71.72, -1964.17,  402.50, 'nach der Weiche: Abstand wächst flach, Richtung hält den Abzweig — kein Straßengabel-Knick'],
  [ -109.87, -2022.43,  402.90, 'Bogen raus aus dem Korridor, dem alten Werksweg westlich folgend'],
  [ -169.84, -2096.43,  404.50, 'durch den Scrub, leichte Steigung zum Abraumhügel'],
  [ -256.59, -2190.24,  407.20, 'Kehre um den Abraum — zu steil/eng für die Hauptstrecke, deshalb Nebenbahn'],
  [ -369.89, -2288.54,  410.80, 'Rampe zur Verladetasche, Hang folgt der alten Grubenbahn'],
  [ -504.84, -2392.39,  415.00, 'kurzer Damm über den Graben vor dem Werksgelände'],
  [ -641.91, -2506.02,  418.80, 'Einfahrt Verladepunkt: Kohle/Erz-Bunker links, tot seit Jahren'],
  [ -747.54, -2616.20,  421.20, 'Verladebühne der aufgegebenen Kupfermine'],
  [ -797.96, -2707.62,  422.00, 'Ausziehgleis vor dem Prellbock — Rangierplatz der alten Verladung'],
  [ -819.73, -2784.76,  422.20, 'Prellbock / Schotterwall — Ende der befahrbaren Welt'],
];

// Korridor-Merkmale als s-Bereiche. Beeinflussen Terrain, Sound und Sicht.
// WARUM (Konzept §12.2): Eine echte Bahn hält Neigung und zwingt das Gelände —
// Einschnitt, Damm, Brücke, Wash. Ohne deklariertes Feature darf das freie
// Gelände seitlich (≈25 m) nicht mehr als ~2,5 m vom Gleis abweichen; sonst
// sieht die Schiene aus, als läge sie zufällig auf dem Rauschen. Jeder Eintrag
// hat einen baulichen Grund (Kommentar). branch filtert Haupt- vs. Nebenstrecke.
export const TRACK_FEATURES = [
  // --- Hauptstrecke --------------------------------------------------------
  // Dammanlauf vor Wash/Weiche: Scrub-Querprofil weicht ab, Strecke hält Höhe
  // aus Cinder Flats (Weiche s≈1840 bleibt tangential unberührt — nur Feature).
  { type: 'embankment',  s0: 1785, s1: 1895, height: 2.8, note: 'Dammanlauf vor Wash/Weiche', branch: BRANCH_MAIN },
  { type: 'wash',        s0: 1900, s1: 2200, note: 'Trockenfluss, Betonwanne (Südwest-Arroyo)', branch: BRANCH_MAIN },
  // Dammlage hinter der Wash-Querung: Scrub-Querprofil steigt, Strecke hält die
  // Wash-Höhe und braucht eine kurze Aufschüttung statt „Gleis im Loch".
  { type: 'embankment',  s0: 2205, s1: 2320, height: 3.0, note: 'Dammlage hinter Wash — Strecke hält Wash-Höhe', branch: BRANCH_MAIN },
  // Anstieg zum Basalt: freies Gelände 3–5 m über Gleis → angeschnitten.
  { type: 'cutting',     s0: 2380, s1: 2680, depth: 4.5, note: 'Einschnitt Scrub-Rücken vor dem Basalt', branch: BRANCH_MAIN },
  // Mulde vor dem großen Basalteinschnitt: kurz aufgeschüttet.
  { type: 'embankment',  s0: 2700, s1: 2840, height: 3.5, note: 'kurzer Damm in der Mulde vor dem Basalteinschnitt', branch: BRANCH_MAIN },
  { type: 'cutting',     s0: 2850, s1: 3200, depth: 7.5, note: 'Einschnitt Basaltrücken', branch: BRANCH_MAIN },
  { type: 'embankment',  s0: 3300, s1: 3700, height: 6.0, note: 'Damm über die Senke', branch: BRANCH_MAIN },
  // Felsrampe + Scheitel: Strecke hält ≤1,5 %; Berg wird kilometerlang angeschnitten
  // (Hanganschnitt), sonst wirkt die Rampe wie prozedurales Rauschen am Gleis.
  // Bis s≈5340, damit der Kieferngrat hinter dem Scheitel mit abgedeckt ist.
  { type: 'cutting',     s0: 3710, s1: 5340, depth: 5.5, note: 'Hanganschnitt Felsrampe/Scheitel — Steigung hält, Berg weicht', branch: BRANCH_MAIN },
  // Vorfeld der Schluchtbrücke: Einschnitt bis an die Widerlager.
  { type: 'cutting',     s0: 5420, s1: 5555, depth: 6.0, note: 'Einschnitt Widerlager-Vorfeld Süd der Schluchtbrücke', branch: BRANCH_MAIN },
  { type: 'bridge',      s0: 5560, s1: 5700, height: 26, note: 'Stahlbrücke über die Schlucht', branch: BRANCH_MAIN },
  // Hinter der Brücke: Auffüllung im Waldhang bis zum kurzen Waldeinschnitt.
  { type: 'embankment',  s0: 5705, s1: 6085, height: 5.0, note: 'Auffüllung hinter der Brücke bis Waldeinschnitt', branch: BRANCH_MAIN },
  { type: 'cutting',     s0: 6100, s1: 6300, depth: 4.0, note: 'kurzer Einschnitt im Wald', branch: BRANCH_MAIN },
  // Nordende hinter Mott: leichter Damm, Meadow-Relief sonst 3 m daneben.
  { type: 'embankment',  s0: 7360, s1: 7430, height: 2.5, note: 'kurzer Damm hinter Mott Junction (Ausziehende)', branch: BRANCH_MAIN },

  // --- Nebenstrecke Kupfermine ---------------------------------------------
  // Direkt nach der Weiche + Scrub-Rücken: Strecke hält Weichenhöhe (~403 m),
  // Gelände will höher → Einschnitt (inkl. s≈35–45, sonst ungestützt).
  { type: 'cutting',     s0: 30,   s1: 480,  depth: 4.5, note: 'Einschnitt Scrub-Rücken westlich der Weiche', branch: BRANCH_MINE },
  // Abraumhang vor dem Graben: Fels-Relief steil, Hanganschnitt bis zum Damm.
  { type: 'cutting',     s0: 500,  s1: 700,  depth: 6.5, note: 'Hanganschnitt Abraumhügel vor dem Graben', branch: BRANCH_MINE },
  // Damm über den Graben *vor* dem Werks-Planum (yardStart ≈ length−450).
  // Früher s 850–1050 — lag im Hofplanum und war als Geländeform unsichtbar.
  { type: 'embankment',  s0: 700,  s1: 820,  height: 5.0, note: 'Damm über den Graben vor dem Werksgelände', branch: BRANCH_MINE },
  // yard: nur Markierung (Planum kommt aus DEAD_END.yard*); kein Terrain-Feature.
  { type: 'yard',        s0: 820,  s1: 1400, note: 'toter Verladepunkt Kupfermine', branch: BRANCH_MINE },
];

// Biome entlang der *Hauptstrecke* (Konzept §12.3: Wüste Süd, Berge/Wald Nord).
// Auf der Nebenstrecke gilt scrub/rock nach s-Nähe zur Weiche.
export const BIOMES = [
  { id: 'playa',   s0:     0, s1: 1800, name: 'Playa' },
  { id: 'scrub',   s0:  1800, s1: 3400, name: 'Buschsteppe' },
  { id: 'rock',    s0:  3400, s1: 4600, name: 'Felsrampe' },
  { id: 'pine',    s0:  4600, s1: 6200, name: 'Kiefernwald' },
  { id: 'meadow',  s0:  6200, s1: 9999, name: 'Nordebene' },
];

// --- Catmull-Rom-Sampling ---------------------------------------------------

function catmull(p0, p1, p2, p3, t) {
  const t2 = t * t, t3 = t2 * t;
  return 0.5 * (
    2 * p1 +
    (-p0 + p2) * t +
    (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
    (-p0 + 3 * p1 - 3 * p2 + p3) * t3
  );
}

const SAMPLE_STEP = 2.0; // m zwischen Stützpunkten der Bogenlängen-Tabelle

function buildSamples(controlPoints) {
  const cp = controlPoints;
  const n = cp.length;
  const raw = [];
  const SUB = 48;
  for (let i = 0; i < n - 1; i++) {
    const p0 = cp[Math.max(0, i - 1)];
    const p1 = cp[i];
    const p2 = cp[i + 1];
    const p3 = cp[Math.min(n - 1, i + 2)];
    const last = i === n - 2;
    for (let j = 0; j < SUB + (last ? 1 : 0); j++) {
      const t = j / SUB;
      raw.push([
        catmull(p0[0], p1[0], p2[0], p3[0], t),
        catmull(p0[1], p1[1], p2[1], p3[1], t),
        catmull(p0[2], p1[2], p2[2], p3[2], t),
      ]);
    }
  }

  const cum = [0];
  for (let i = 1; i < raw.length; i++) {
    const dx = raw[i][0] - raw[i - 1][0];
    const dz = raw[i][1] - raw[i - 1][1];
    cum.push(cum[i - 1] + Math.hypot(dx, dz));
  }
  const total = cum[cum.length - 1];

  const count = Math.floor(total / SAMPLE_STEP) + 1;
  const xs = new Float64Array(count);
  const zs = new Float64Array(count);
  const ys = new Float64Array(count);
  let cursor = 1;
  for (let k = 0; k < count; k++) {
    const s = Math.min(total, k * SAMPLE_STEP);
    while (cursor < cum.length - 1 && cum[cursor] < s) cursor++;
    const c0 = cum[cursor - 1], c1 = cum[cursor];
    const f = c1 > c0 ? (s - c0) / (c1 - c0) : 0;
    const a = raw[cursor - 1], b = raw[cursor];
    xs[k] = a[0] + (b[0] - a[0]) * f;
    zs[k] = a[1] + (b[1] - a[1]) * f;
    ys[k] = a[2] + (b[2] - a[2]) * f;
  }
  return {
    xs, ys, zs, count,
    length: (count - 1) * SAMPLE_STEP,
    step: SAMPLE_STEP,
    controlPoints: controlPoints,
  };
}

// Hauptstrecke zuerst.
const MAIN = buildSamples(MAIN_CONTROL_POINTS);

// Weiche: nächster Stützpunkt zur Ziel-Bogenlänge.
export const JUNCTION_MAIN_S = Math.round(JUNCTION_TARGET_S / SAMPLE_STEP) * SAMPLE_STEP;
const _juncI = Math.min(MAIN.count - 1, Math.round(JUNCTION_MAIN_S / SAMPLE_STEP));
const JUNCTION_POS = {
  x: MAIN.xs[_juncI],
  z: MAIN.zs[_juncI],
  y: MAIN.ys[_juncI],
};

// Nebenstrecke: P0 exakt auf die Weiche setzen, Rest aus den Zielen.
const MINE_CONTROL_POINTS = MINE_CONTROL_TARGETS.map((p, i) => {
  if (i === 0) {
    return [JUNCTION_POS.x, JUNCTION_POS.z, JUNCTION_POS.y, p[3]];
  }
  return p.slice();
});
const MINE = buildSamples(MINE_CONTROL_POINTS);

/** Alle befahrbaren Strecken. */
export const BRANCHES = Object.freeze({
  [BRANCH_MAIN]: Object.freeze({
    id: BRANCH_MAIN,
    length: MAIN.length,
    sampleCount: MAIN.count,
    sampleStep: MAIN.step,
    deadEnd: false,
    controlPointCount: MAIN_CONTROL_POINTS.length,
  }),
  [BRANCH_MINE]: Object.freeze({
    id: BRANCH_MINE,
    length: MINE.length,
    sampleCount: MINE.count,
    sampleStep: MINE.step,
    deadEnd: true,
    controlPointCount: MINE_CONTROL_POINTS.length,
    // Weiche: s=0 der Nebenstrecke = JUNCTION_MAIN_S der Hauptstrecke.
    junctionMainS: JUNCTION_MAIN_S,
    junctionMineS: 0,
  }),
});

export const BRANCH_IDS = Object.freeze([BRANCH_MAIN, BRANCH_MINE]);

const SAMPLES = {
  [BRANCH_MAIN]: MAIN,
  [BRANCH_MINE]: MINE,
};

function resolveBranch(branch) {
  if (branch == null || branch === BRANCH_MAIN) return BRANCH_MAIN;
  if (SAMPLES[branch]) return branch;
  return BRANCH_MAIN;
}

function samplesOf(branch) {
  return SAMPLES[resolveBranch(branch)];
}

function clampS(s, S) {
  return s < 0 ? 0 : s > S.length ? S.length : s;
}

// Abwärtskompatibel: TRACK_LENGTH = Länge der Hauptstrecke.
export const TRACK_LENGTH = MAIN.length;
export const MINE_TRACK_LENGTH = MINE.length;

/** Länge einer Strecke (default: Hauptstrecke). */
export function trackLength(branch = BRANCH_MAIN) {
  return samplesOf(branch).length;
}

/** Position auf der Gleismitte bei Bogenlänge s. Schreibt in out, falls gegeben. */
export function trackPoint(s, out = { x: 0, y: 0, z: 0 }, branch = BRANCH_MAIN) {
  const S = samplesOf(branch);
  const sc = clampS(s, S);
  const fi = sc / S.step;
  const i = Math.min(S.count - 2, Math.floor(fi));
  const f = fi - i;
  out.x = S.xs[i] + (S.xs[i + 1] - S.xs[i]) * f;
  out.y = S.ys[i] + (S.ys[i + 1] - S.ys[i]) * f;
  out.z = S.zs[i] + (S.zs[i + 1] - S.zs[i]) * f;
  return out;
}

/** Normierte Tangente (Fahrtrichtung bei steigendem s). */
export function trackTangent(s, out = { x: 0, y: 0, z: 0 }, branch = BRANCH_MAIN) {
  const S = samplesOf(branch);
  const sc = clampS(s, S);
  const i = Math.min(S.count - 2, Math.max(0, Math.floor(sc / S.step)));
  let dx = S.xs[i + 1] - S.xs[i];
  let dy = S.ys[i + 1] - S.ys[i];
  let dz = S.zs[i + 1] - S.zs[i];
  const len = Math.hypot(dx, dy, dz) || 1;
  out.x = dx / len; out.y = dy / len; out.z = dz / len;
  return out;
}

/** Rechte Normale in der XZ-Ebene (Blickrichtung = Tangente, rechts davon). */
export function trackRight(s, out = { x: 0, y: 0, z: 0 }, branch = BRANCH_MAIN) {
  const t = trackTangent(s, { x: 0, y: 0, z: 0 }, branch);
  let rx = t.z, rz = -t.x;
  const len = Math.hypot(rx, rz) || 1;
  out.x = rx / len; out.y = 0; out.z = rz / len;
  return out;
}

/** Vollständiger Frame: Punkt + Tangente + Rechts. Für Waggon-Platzierung. */
export function trackFrame(s, out = null, branch = BRANCH_MAIN) {
  const o = out ?? {
    pos: { x: 0, y: 0, z: 0 },
    tangent: { x: 0, y: 0, z: 0 },
    right: { x: 0, y: 0, z: 0 },
    heading: 0,
  };
  trackPoint(s, o.pos, branch);
  trackTangent(s, o.tangent, branch);
  trackRight(s, o.right, branch);
  o.heading = Math.atan2(o.tangent.x, o.tangent.z);
  return o;
}

/** Steigung an der Stelle s (dy/ds). Negativ = Gefälle in Fahrtrichtung. */
export function trackGrade(s, branch = BRANCH_MAIN) {
  const S = samplesOf(branch);
  const sc = clampS(s, S);
  const i = Math.min(S.count - 2, Math.max(0, Math.floor(sc / S.step)));
  return (S.ys[i + 1] - S.ys[i]) / S.step;
}

/** Krümmung (1/m, Vorzeichen = Richtung) für Überhöhung und Sichtweite. */
export function trackCurvature(s, branch = BRANCH_MAIN) {
  const h = 12;
  const a = trackTangent(s - h, { x: 0, y: 0, z: 0 }, branch);
  const b = trackTangent(s + h, { x: 0, y: 0, z: 0 }, branch);
  const cross = a.x * b.z - a.z * b.x;
  const dot = Math.max(-1, Math.min(1, a.x * b.x + a.z * b.z));
  const angle = Math.atan2(cross, dot);
  return angle / (2 * h);
}

/**
 * Projiziert eine Weltposition auf die Strecke.
 * Liefert s, lateral, dy, distance und branch.
 * Mit branch-Argument: nur diese Strecke. Ohne: nächstgelegene über alle.
 * hint ist Bogenlänge auf der gewählten/vermuteten Strecke.
 */
export function projectToTrack(x, z, hint = -1, branch = null) {
  if (branch != null) {
    return projectToOne(x, z, hint, resolveBranch(branch));
  }
  // Alle Strecken prüfen, nächste wählen. Bei der Weiche gewinnt die
  // Hauptstrecke bei Gleichstand (Stabilität für bestehenden Client-Code).
  let best = null;
  for (const id of BRANCH_IDS) {
    const p = projectToOne(x, z, id === BRANCH_MAIN ? hint : -1, id);
    if (!best || p.distance < best.distance - 0.05
      || (Math.abs(p.distance - best.distance) <= 0.05 && id === BRANCH_MAIN)) {
      best = p;
    }
  }
  return best;
}

function projectToOne(x, z, hint, branch) {
  const S = samplesOf(branch);
  let bestI = 0, bestD = Infinity;
  if (hint >= 0) {
    const hi = Math.floor(clampS(hint, S) / S.step);
    const lo = Math.max(0, hi - 60), up = Math.min(S.count - 1, hi + 60);
    for (let i = lo; i <= up; i++) {
      const dx = x - S.xs[i], dz = z - S.zs[i];
      const d = dx * dx + dz * dz;
      if (d < bestD) { bestD = d; bestI = i; }
    }
    if (bestI <= lo + 1 || bestI >= up - 1) bestD = Infinity;
  }
  if (bestD === Infinity) {
    const stride = 8;
    for (let i = 0; i < S.count; i += stride) {
      const dx = x - S.xs[i], dz = z - S.zs[i];
      const d = dx * dx + dz * dz;
      if (d < bestD) { bestD = d; bestI = i; }
    }
    const lo = Math.max(0, bestI - stride), up = Math.min(S.count - 1, bestI + stride);
    bestD = Infinity;
    for (let i = lo; i <= up; i++) {
      const dx = x - S.xs[i], dz = z - S.zs[i];
      const d = dx * dx + dz * dz;
      if (d < bestD) { bestD = d; bestI = i; }
    }
  }

  const i = Math.min(S.count - 2, bestI);
  const ax = S.xs[i], az = S.zs[i];
  const bx = S.xs[i + 1], bz = S.zs[i + 1];
  const ex = bx - ax, ez = bz - az;
  const elen2 = ex * ex + ez * ez || 1;
  let t = ((x - ax) * ex + (z - az) * ez) / elen2;
  t = Math.max(-1, Math.min(2, t));
  const s = clampS((i + t) * S.step, S);

  const p = trackPoint(s, { x: 0, y: 0, z: 0 }, branch);
  const r = trackRight(s, { x: 0, y: 0, z: 0 }, branch);
  const lateral = (x - p.x) * r.x + (z - p.z) * r.z;
  return {
    s, lateral, trackY: p.y,
    distance: Math.hypot(x - p.x, z - p.z),
    branch,
  };
}

/** Feature an der Stelle s (oder null). Optional branch-gefiltert. */
export function featureAt(s, branch = BRANCH_MAIN) {
  const b = resolveBranch(branch);
  for (const f of TRACK_FEATURES) {
    if ((f.branch ?? BRANCH_MAIN) !== b) continue;
    if (s >= f.s0 && s <= f.s1) return f;
  }
  return null;
}

export function biomeAt(s, branch = BRANCH_MAIN) {
  if (resolveBranch(branch) === BRANCH_MINE) {
    // Nebenstrecke liegt in scrub/rock-Gürtel der Hauptstrecke.
    if (s < 600) return BIOMES[1]; // scrub
    return { id: 'rock', s0: 0, s1: 9999, name: 'Abraum / Fels' };
  }
  for (const b of BIOMES) {
    if (s >= b.s0 && s < b.s1) return b;
  }
  return BIOMES[BIOMES.length - 1];
}

/**
 * Höhe der Schienenoberkante inkl. Überhöhung an der Stelle s, seitlich um
 * lateral verschoben. Nutzt Bogen-Überhöhung, damit Waggons in Kurven kippen.
 */
export function railTopY(s, lateral = 0, branch = BRANCH_MAIN) {
  const p = trackPoint(s, { x: 0, y: 0, z: 0 }, branch);
  const cant = trackCant(s, branch);
  return p.y + 0.18 + lateral * cant;
}

/** Überhöhung als Neigung (rad-artig, klein). */
export function trackCant(s, branch = BRANCH_MAIN) {
  const c = trackCurvature(s, branch);
  return Math.max(-0.06, Math.min(0.06, -c * 70));
}

// --- Bahnhöfe / Orte --------------------------------------------------------
// Zwei Höfe an den Enden der Hauptstrecke + ein toter Verladepunkt an der
// Sackgasse. STATIONS bleibt die Liste der *beiden* Haupt-Höfe (bestehende
// Prüfungen und UI-Code erwarten length === 2). Der tote Ort ist DEAD_END.

export const STATIONS = [
  {
    id: 'south',
    codeName: 'Cinder Flats',
    branch: BRANCH_MAIN,
    s: 260,
    yardStart: 60,
    yardEnd: 620,
    sidings: [
      { id: 'south-a', lateral: -9.0, s0: 90, s1: 600, note: 'Abstellgleis West' },
      { id: 'south-b', lateral:  9.0, s0: 120, s1: 560, note: 'Ladegleis Ost, Kohlebunker' },
    ],
    platformLateral: 6.2,
    hasWaterTower: false,
    activity: 0.55, // belebter Hof (Konzept §11): Lichter, Leute, Rangieren
  },
  {
    id: 'north',
    codeName: 'Mott Junction',
    branch: BRANCH_MAIN,
    s: 6950,
    yardStart: 6700,
    yardEnd: 7200,
    sidings: [
      { id: 'north-a', lateral: -8.5, s0: 6720, s1: 7180, note: 'Ausziehgleis' },
      { id: 'north-dead', lateral: 15.0, s0: 6980, s1: 7150, note: 'totes Nebengleis (Konzept §7: viel Zeit, niemand sieht es)' },
    ],
    platformLateral: -6.0,
    hasWaterTower: true,
    activity: 0.2, // ruhiger, halb toter Hof
  },
];

/** Toter Verladepunkt am Ende der Nebenstrecke — kein Verkehr außer dem einen Zug. */
export const DEAD_END = Object.freeze({
  id: 'mine',
  codeName: 'Copper Spur',
  branch: BRANCH_MINE,
  // Standplatz der Zugspitze: weit genug vor dem Prellbock für den ganzen Zug.
  // Offset so gewählt, dass die Hofkante im Fels-Biome keine Rausch-Klippe trifft
  // (terrain ridge-noise; s = length-220 lag auf einem Grat und brach check-world).
  s: Math.max(200, MINE.length - 200),
  yardStart: Math.max(0, MINE.length - 450),
  yardEnd: MINE.length,
  sidings: [],
  platformLateral: 5.5,
  hasWaterTower: false,
  activity: 0.0, // tot (Konzept §2.1 / §11)
  deadEnd: true,
});

/** Weiche Hauptstrecke → Nebenstrecke. */
export const JUNCTION = Object.freeze({
  id: 'mine-switch',
  fromBranch: BRANCH_MAIN,
  fromS: JUNCTION_MAIN_S,
  toBranch: BRANCH_MINE,
  toS: 0,
  note: 'Abzweig zur aufgegebenen Kupfermine — nur zurück, wo man herkam',
});

export function stationById(id) {
  if (id === DEAD_END.id) return DEAD_END;
  return STATIONS.find((st) => st.id === id) ?? null;
}

/** Bahnhof, in dessen Hofbereich s auf der gegebenen Strecke liegt (oder null). */
export function stationAt(s, branch = BRANCH_MAIN) {
  const b = resolveBranch(branch);
  if (b === BRANCH_MINE) {
    if (s >= DEAD_END.yardStart && s <= DEAD_END.yardEnd) return DEAD_END;
    return null;
  }
  for (const st of STATIONS) {
    if (s >= st.yardStart && s <= st.yardEnd) return st;
  }
  return null;
}

export function isInYard(s, branch = BRANCH_MAIN) {
  return stationAt(s, branch) !== null;
}

/** Alle Orte inkl. Sackgasse (für Debug / spätere Systeme). */
export function allPlaces() {
  return [...STATIONS, DEAD_END];
}

export const TRACK_INFO = Object.freeze({
  length: TRACK_LENGTH,
  mineLength: MINE.length,
  gauge: RAIL_GAUGE,
  sampleStep: MAIN.step,
  sampleCount: MAIN.count,
  controlPointCount: MAIN_CONTROL_POINTS.length,
  junctionMainS: JUNCTION_MAIN_S,
  branches: BRANCH_IDS.slice(),
});

// Kontrollpunkte nur für Debug/Tests exportieren (nicht mutieren).
export const _CONTROL_POINTS_MAIN = MAIN_CONTROL_POINTS;
export const _CONTROL_POINTS_MINE = MINE_CONTROL_POINTS;
